import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Phone, MessageCircle, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

gsap.registerPlugin(ScrollTrigger);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactSection() {
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    car_model: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.contact-content',
        { opacity: 0, y: 60 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Thank you! We will contact you shortly.');
      setFormData({ name: '', phone: '', car_model: '', message: '' });
      
      // Reset submitted state after 3 seconds
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="contact" 
      className="relative py-20 lg:py-32 overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0A0A0A] to-[#050505]"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <div className="contact-content text-center mb-16">
          <span className="inline-block text-cyan-400 text-sm font-semibold tracking-[0.3em] uppercase mb-4">
            Get In Touch
          </span>
          <h2 className="font-orbitron text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-wider mb-6">
            Contact <span className="gradient-text">Us</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Ready to fix your auto glass? Reach out to us anytime!
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Contact info */}
          <div className="contact-content space-y-8">
            {/* Contact cards */}
            <div className="space-y-4">
              {/* Phone */}
              <a 
                href="tel:+917300677651"
                className="glass-card flex items-center gap-4 p-5 rounded-xl hover:border-cyan-500/50 transition-all duration-300 group"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                  <Phone className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Call Us Anytime</p>
                  <p className="text-white text-xl font-semibold">+91 7300677651</p>
                </div>
              </a>
              
              {/* WhatsApp */}
              <a 
                href="https://wa.me/917300677651"
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card flex items-center gap-4 p-5 rounded-xl hover:border-green-500/50 transition-all duration-300 group"
                data-testid="contact-whatsapp"
              >
                <div className="w-14 h-14 bg-green-500/10 rounded-xl flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                  <MessageCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">WhatsApp Us</p>
                  <p className="text-white text-xl font-semibold">+91 7300677651</p>
                </div>
              </a>
              
              {/* Location */}
              <div className="glass-card flex items-center gap-4 p-5 rounded-xl">
                <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Our Location</p>
                  <p className="text-white text-lg font-semibold">Transport Nagar, Rajpur Road, Dehradun-248001, Uttarakhand</p>
                  <p className="text-gray-500 text-sm">Uttarakhand, India</p>
                </div>
              </div>
            </div>
            
            {/* Business hours */}
            <div className="glass-card p-6 rounded-xl">
              <h3 className="font-orbitron text-lg font-bold text-white uppercase mb-4">Business Hours</h3>
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                <span className="text-green-400 font-medium">Open 24 Hours</span>
                <span className="text-gray-500">|</span>
                <span className="text-gray-400">7 Days a Week</span>
              </div>
            </div>
          </div>
          
          {/* Contact form */}
          <div className="contact-content">
            <form 
              ref={formRef}
              onSubmit={handleSubmit}
              className="glass-card p-6 lg:p-8 rounded-2xl space-y-6"
              data-testid="contact-form"
            >
              <h3 className="font-orbitron text-xl font-bold text-white uppercase mb-6">
                Get Free Quote
              </h3>
              
              {/* Name field */}
              <div className="space-y-2">
                <label className="text-gray-400 text-sm">Name *</label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  className="contact-input bg-white/5 border-white/10 focus:border-cyan-500 h-12 rounded-lg"
                  required
                  data-testid="input-name"
                />
              </div>
              
              {/* Phone field */}
              <div className="space-y-2">
                <label className="text-gray-400 text-sm">Phone *</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your Phone Number"
                  className="contact-input bg-white/5 border-white/10 focus:border-cyan-500 h-12 rounded-lg"
                  required
                  data-testid="input-phone"
                />
              </div>
              
              {/* Car model field */}
              <div className="space-y-2">
                <label className="text-gray-400 text-sm">Car Model</label>
                <Input
                  type="text"
                  name="car_model"
                  value={formData.car_model}
                  onChange={handleChange}
                  placeholder="e.g., Honda City, Maruti Swift"
                  className="contact-input bg-white/5 border-white/10 focus:border-cyan-500 h-12 rounded-lg"
                  data-testid="input-car-model"
                />
              </div>
              
              {/* Message field */}
              <div className="space-y-2">
                <label className="text-gray-400 text-sm">Message</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe your issue..."
                  className="contact-input bg-white/5 border-white/10 focus:border-cyan-500 min-h-[120px] rounded-lg resize-none"
                  data-testid="input-message"
                />
              </div>
              
              {/* Submit button */}
              <Button
                type="submit"
                disabled={isSubmitting || submitted}
                className={`w-full h-14 font-orbitron font-bold uppercase tracking-widest text-lg transition-all duration-300 ${
                  submitted 
                    ? 'bg-green-500 hover:bg-green-500' 
                    : 'bg-cyan-500 hover:bg-cyan-400 hover:shadow-[0_0_30px_rgba(0,212,255,0.6)]'
                } text-black`}
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </span>
                ) : submitted ? (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Sent Successfully!
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
